import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Avatar, Badge, Button, Card, Col, ConfigProvider, Drawer, Input,
    Row, Select, Space, Table, Tag, Timeline, Tooltip, Typography, Segmented, Empty
} from "antd";
import {
    ApiOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    EyeOutlined,
    FilterOutlined,
    LoginOutlined,
    ReloadOutlined,
    SearchOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    UnorderedListOutlined,
    AppstoreOutlined,
    UserOutlined,
    HistoryOutlined,
    MessageOutlined,
} from "@ant-design/icons";
import Column from "antd/es/table/Column";
import { useFetchAdminUserList } from "../services/user.service";
import { storage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../helpers/common";
import Pagination from "../helpers/pagination";
import { useFetchUserActivityLogList } from "../services/activitylog.service";
import { IUserActivityLog } from "../types/useractivitylogs";
import { message } from "antd";

const { Text, Title } = Typography;

// ── Design tokens ─────────────────────────────────────────────────────────────
const CARD_BORDER = "#e8eaf0";
const TEXT_PRIMARY = "#1e293b";
const TEXT_MUTED = "#64748b";

const HTTP_TAG_COLORS: Record<string, string> = {
    GET: "blue", POST: "green", PATCH: "orange", PUT: "purple", DELETE: "red",
};

const ROLE_CONFIG: Record<string, { color: string; bg: string }> = {
    SuperAdmin: { color: "#ef4444", bg: "#fef2f2" },
    Admin:      { color: "#6366f1", bg: "#eef2ff" },
    Traveller:  { color: "#0891b2", bg: "#ecfeff" },
    Attendant:  { color: "#10b981", bg: "#f0fdf4" },
};

const MODULE_ICONS: Record<string, React.ReactNode> = {
    Trip:        <HistoryOutlined />,
    TripPayment: <ApiOutlined />,
    Auth:        <LoginOutlined />,
    Chat:        <MessageOutlined />,
    User:        <UserOutlined />,
};

const AVATAR_PALETTE = ["#6366f1","#0891b2","#f59e0b","#10b981","#f43f5e","#a78bfa","#06b6d4"];
const avatarColor = (name: string) => {
    if (!name) return AVATAR_PALETTE[0];
    const s = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return AVATAR_PALETTE[s % AVATAR_PALETTE.length];
};
const initials = (name: string) => {
    if (!name) return "?";
    const p = name.trim().split(" ");
    return (p.length >= 2 ? `${p[0][0]}${p[1][0]}` : p[0].substring(0, 2)).toUpperCase();
};
const isLogin = (action: string) => /login|signin|sign.in|authenticate/i.test(action ?? "");

// ── StatCard ──────────────────────────────────────────────────────────────────
const StatCard: React.FC<{ title: string; value: string | number; sub?: string; icon: React.ReactNode; color: string }> = ({ title, value, sub, icon, color }) => (
    <Card style={{ borderRadius: 16, border: `1px solid ${CARD_BORDER}`, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
                <Text style={{ color: TEXT_MUTED, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</Text>
                <div style={{ fontSize: 26, fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.3, marginTop: 4 }}>{value}</div>
                {sub && <Text style={{ color: TEXT_MUTED, fontSize: 12, marginTop: 2, display: "block" }}>{sub}</Text>}
            </div>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color }}>
                {icon}
            </div>
        </div>
    </Card>
);

// ── Main Component ────────────────────────────────────────────────────────────
const ActivityLogs: React.FC = () => {
    const navigate = useNavigate();

    const [admins, setAdmins]                 = useState<any[]>([]);
    const [userlogs, setUserlogs]             = useState<IUserActivityLog[]>([]);
    const [loading, setLoading]               = useState(false);
    const [selectedAdmin, setSelectedAdmin]   = useState<string | null>(null);
    const [roleFilter, setRoleFilter]         = useState<string>("All");
    const [methodFilter, setMethodFilter]     = useState<string>("All");
    const [moduleFilter, setModuleFilter]     = useState<string>("All");
    const [viewMode, setViewMode]             = useState<"table" | "timeline">("table");
    const [drawerVisible, setDrawerVisible]   = useState(false);
    const [drawerRecord, setDrawerRecord]     = useState<any>(null);
    const [totalRecords, setTotalRecords]     = useState(0);
    const [lastRefreshed, setLastRefreshed]   = useState<Date>(new Date());

    const pageNumber   = useRef(1);
    const [pageSize]   = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const searchValue  = useRef<string>("");

    // ── Fetch admin list ──────────────────────────────────────────────────────
    const fetchAdminListMutation = useFetchAdminUserList({
        onSuccess: (res: any) => { if (res?.statusCode === 200) setAdmins(res.data); },
        onError:   () => message.error("Failed to load admin users"),
    });

    // ── Fetch logs ────────────────────────────────────────────────────────────
    const fetchLogs = (userId: string | null = selectedAdmin) => {
        setLoading(true);
        fetchLogsMutation.mutate({ userId, searchFilter: searchValue.current ?? "", pageNumber: pageNumber.current, pageSize });
    };

    const fetchLogsMutation = useFetchUserActivityLogList({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) {
                setUserlogs(res.data ?? []);
                const total = res.data?.[0]?.recordCount ?? 0;
                setTotalRecords(total);
                setTotalPages(Math.ceil(total / pageSize));
            } else {
                setUserlogs([]);
                setTotalPages(1);
                setTotalRecords(0);
            }
            setLastRefreshed(new Date());
            setLoading(false);
        },
        onError: () => { setLoading(false); message.error("Failed to load activity logs"); },
    });

    useEffect(() => {
        const role = parseInt(storage.getUserRoleId() ?? "0");
        if (role !== 1) navigate("/");
        fetchAdminListMutation.mutate({ statusFilter: "All", searchFilter: "", pageNumber: 1, pageSize: 100 });
        fetchLogs(null);
    }, []);

    // ── Client-side filtered data ─────────────────────────────────────────────
    const displayLogs = useMemo(() => {
        return userlogs.filter(l => {
            if (roleFilter   !== "All" && l.userRoleName !== roleFilter)   return false;
            if (methodFilter !== "All" && l.httpMethod   !== methodFilter) return false;
            if (moduleFilter !== "All" && l.moduleName   !== moduleFilter) return false;
            return true;
        });
    }, [userlogs, roleFilter, methodFilter, moduleFilter]);

    // ── Derived stats ─────────────────────────────────────────────────────────
    const uniqueUsers  = useMemo(() => new Set(userlogs.map(l => l.userId)).size, [userlogs]);
    const loginCount   = useMemo(() => userlogs.filter(l => isLogin(l.actionName)).length, [userlogs]);
    const topModule    = useMemo(() => {
        const freq: Record<string, number> = {};
        userlogs.forEach(l => { freq[l.moduleName] = (freq[l.moduleName] ?? 0) + 1; });
        return Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";
    }, [userlogs]);
    const uniqueModules = useMemo(() => [...new Set(userlogs.map(l => l.moduleName))], [userlogs]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const onSearch = (value: string) => {
        pageNumber.current = 1;
        searchValue.current = value;
        fetchLogs();
    };
    const onAdminSelect = (value: string | null) => {
        setSelectedAdmin(value);
        pageNumber.current = 1;
        fetchLogs(value);
    };
    const handlePageChange = (newPage: number) => {
        pageNumber.current = newPage;
        fetchLogs();
    };
    const onRefresh = () => {
        pageNumber.current = 1;
        fetchLogs();
    };
    const openDrawer = (record: any) => { setDrawerRecord(record); setDrawerVisible(true); };

    const renderPayload = (dataStr: any) => {
        if (!dataStr) return <Text type="secondary">No payload</Text>;
        try {
            const parsed = typeof dataStr === "string" ? JSON.parse(dataStr) : dataStr;
            return (
                <pre style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 14px", fontSize: 12, overflowX: "auto", maxHeight: 400, whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0, border: "1px solid #e2e8f0" }}>
                    {JSON.stringify(parsed, null, 2)}
                </pre>
            );
        } catch {
            return <Text style={{ fontSize: 13 }}>{String(dataStr)}</Text>;
        }
    };

    // ── Timeline dot color by HTTP method ─────────────────────────────────────
    const timelineDotColor = (method: string) =>
        ({ GET: "#3b82f6", POST: "#10b981", PATCH: "#f59e0b", PUT: "#8b5cf6", DELETE: "#ef4444" }[method] ?? "#94a3b8");

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ background: "#f5f6fa", minHeight: "100vh", padding: "0 0 32px" }}>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <Title level={3} style={{ margin: 0, color: TEXT_PRIMARY }}>Activity Logs</Title>
                    <Text style={{ color: TEXT_MUTED, fontSize: 13 }}>
                        Last refreshed: {lastRefreshed.toLocaleTimeString()}
                    </Text>
                </div>
                <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} style={{ borderRadius: 8 }}>
                    Refresh
                </Button>
            </div>

            {/* ── Stats row ───────────────────────────────────────────────── */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard title="Total Events" value={totalRecords.toLocaleString()} sub="across all pages" icon={<ThunderboltOutlined />} color="#6366f1" />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard title="Login Events" value={loginCount} sub="in current view" icon={<LoginOutlined />} color="#10b981" />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard title="Unique Users" value={uniqueUsers} sub="in current view" icon={<TeamOutlined />} color="#0891b2" />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard title="Top Module" value={topModule} sub="most active" icon={<ApiOutlined />} color="#f59e0b" />
                </Col>
            </Row>

            {/* ── Filters ─────────────────────────────────────────────────── */}
            <Card style={{ borderRadius: 16, border: `1px solid ${CARD_BORDER}`, marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }} bodyStyle={{ padding: "16px 20px" }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <FilterOutlined style={{ color: TEXT_MUTED, fontSize: 16 }} />
                    <Input.Search
                        placeholder="Search by user, module, action…"
                        allowClear
                        size="middle"
                        style={{ width: 260 }}
                        onSearch={onSearch}
                        prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />}
                    />
                    <Select
                        placeholder="Filter by user"
                        allowClear
                        size="middle"
                        style={{ width: 220 }}
                        onChange={onAdminSelect}
                        options={admins.map(a => ({ label: `${a.fullName} (${a.email})`, value: a.adminUserID }))}
                    />
                    <Select
                        value={roleFilter}
                        size="middle"
                        style={{ width: 150 }}
                        onChange={setRoleFilter}
                        options={[
                            { label: "All Roles", value: "All" },
                            { label: "SuperAdmin",  value: "SuperAdmin" },
                            { label: "Admin",       value: "Admin" },
                            { label: "Traveller",   value: "Traveller" },
                            { label: "Attendant",   value: "Attendant" },
                        ]}
                    />
                    <Select
                        value={methodFilter}
                        size="middle"
                        style={{ width: 140 }}
                        onChange={setMethodFilter}
                        options={[
                            { label: "All Methods", value: "All" },
                            ...["GET","POST","PATCH","PUT","DELETE"].map(m => ({ label: m, value: m }))
                        ]}
                    />
                    <Select
                        value={moduleFilter}
                        size="middle"
                        style={{ width: 160 }}
                        onChange={setModuleFilter}
                        options={[
                            { label: "All Modules", value: "All" },
                            ...uniqueModules.map(m => ({ label: m, value: m }))
                        ]}
                    />
                    <div style={{ marginLeft: "auto" }}>
                        <Segmented
                            value={viewMode}
                            onChange={(v) => setViewMode(v as any)}
                            options={[
                                { label: <Tooltip title="Table View"><UnorderedListOutlined /></Tooltip>, value: "table" },
                                { label: <Tooltip title="Timeline View"><AppstoreOutlined /></Tooltip>, value: "timeline" },
                            ]}
                        />
                    </div>
                </div>
                {(roleFilter !== "All" || methodFilter !== "All" || moduleFilter !== "All") && (
                    <div style={{ marginTop: 10, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <Text style={{ color: TEXT_MUTED, fontSize: 12 }}>Active filters:</Text>
                        {roleFilter   !== "All" && <Tag closable onClose={() => setRoleFilter("All")}   color="purple">{roleFilter}</Tag>}
                        {methodFilter !== "All" && <Tag closable onClose={() => setMethodFilter("All")} color={HTTP_TAG_COLORS[methodFilter] ?? "default"}>{methodFilter}</Tag>}
                        {moduleFilter !== "All" && <Tag closable onClose={() => setModuleFilter("All")} color="cyan">{moduleFilter}</Tag>}
                        <Button type="link" size="small" style={{ padding: 0, height: "auto", fontSize: 12 }} onClick={() => { setRoleFilter("All"); setMethodFilter("All"); setModuleFilter("All"); }}>
                            Clear all
                        </Button>
                    </div>
                )}
            </Card>

            {/* ── Table / Timeline ────────────────────────────────────────── */}
            <Card style={{ borderRadius: 16, border: `1px solid ${CARD_BORDER}`, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }} bodyStyle={{ padding: 0 }}>

                {viewMode === "table" ? (
                    <ConfigProvider>
                        <Table
                            dataSource={displayLogs}
                            className="userlist-tabs-table"
                            loading={loading}
                            rowKey="activityLogId"
                            pagination={false}
                            style={{ borderRadius: 16, overflow: "hidden" }}
                            rowClassName={(_, idx) => idx % 2 === 0 ? "" : "bg-gray-50"}
                        >
                            <Column
                                title="User"
                                key="user"
                                align="left"
                                width={220}
                                render={(_: any, record: any) => (
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <Avatar style={{ background: avatarColor(record.fullName), fontWeight: 700, flexShrink: 0 }} size={36}>
                                            {initials(record.fullName)}
                                        </Avatar>
                                        <div>
                                            <div style={{ fontWeight: 600, color: TEXT_PRIMARY, fontSize: 13, lineHeight: 1.3 }}>{record.fullName || <Text type="secondary">Unknown</Text>}</div>
                                            <div style={{ fontSize: 11, color: TEXT_MUTED }}>{record.email}</div>
                                        </div>
                                    </div>
                                )}
                            />
                            <Column
                                title="Role"
                                dataIndex="userRoleName"
                                key="role"
                                align="center"
                                width={120}
                                render={(role: string) => {
                                    const cfg = ROLE_CONFIG[role] ?? { color: "#64748b", bg: "#f1f5f9" };
                                    return (
                                        <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 20, padding: "2px 12px", fontSize: 12, fontWeight: 600 }}>
                                            {role}
                                        </span>
                                    );
                                }}
                            />
                            <Column
                                title="Module"
                                dataIndex="moduleName"
                                key="module"
                                align="center"
                                width={140}
                                render={(mod: string) => (
                                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                                        <span style={{ color: "#6366f1" }}>{MODULE_ICONS[mod] ?? <ApiOutlined />}</span>
                                        <span style={{ fontSize: 13 }}>{mod}</span>
                                    </span>
                                )}
                            />
                            <Column
                                title="Action"
                                dataIndex="actionName"
                                key="action"
                                align="center"
                                render={(action: string) => (
                                    <Tooltip title={action}>
                                        <span style={{ fontSize: 13, maxWidth: 180, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {isLogin(action) && <LoginOutlined style={{ color: "#10b981", marginRight: 5 }} />}
                                            {action}
                                        </span>
                                    </Tooltip>
                                )}
                            />
                            <Column
                                title="Method"
                                dataIndex="httpMethod"
                                key="httpMethod"
                                align="center"
                                width={100}
                                render={(method: string) => (
                                    <Tag color={HTTP_TAG_COLORS[method] ?? "default"} style={{ fontWeight: 700, letterSpacing: 0.5, borderRadius: 6 }}>{method}</Tag>
                                )}
                            />
                            <Column
                                title="Timestamp"
                                dataIndex="createdAt"
                                key="createdAt"
                                align="center"
                                width={170}
                                render={(val: any) => (
                                    <span style={{ color: TEXT_MUTED, fontSize: 12 }}>
                                        <ClockCircleOutlined style={{ marginRight: 5 }} />
                                        {formatDateTime(val, "DD/MM/YYYY HH:mm AMPM")}
                                    </span>
                                )}
                            />
                            <Column
                                title=""
                                key="detail"
                                align="center"
                                width={60}
                                render={(_: any, record: any) => (
                                    <Tooltip title="View details">
                                        <Button
                                            size="small"
                                            shape="circle"
                                            icon={<EyeOutlined />}
                                            onClick={() => openDrawer(record)}
                                            style={{ border: "1px solid #e2e8f0" }}
                                        />
                                    </Tooltip>
                                )}
                            />
                        </Table>
                        <div style={{ padding: "16px 20px", borderTop: `1px solid ${CARD_BORDER}` }}>
                            <Pagination currentPage={pageNumber.current} totalPages={totalPages} onPageChange={handlePageChange} />
                        </div>
                    </ConfigProvider>
                ) : (
                    /* ── Timeline view ────────────────────────────── */
                    <div style={{ padding: "24px 32px" }}>
                        {loading ? (
                            <div style={{ textAlign: "center", padding: 48 }}><span>Loading…</span></div>
                        ) : displayLogs.length === 0 ? (
                            <Empty description="No activity logs found" />
                        ) : (
                            <>
                                <Timeline
                                    items={displayLogs.map(log => ({
                                        dot: (
                                            <div style={{ width: 10, height: 10, borderRadius: "50%", background: timelineDotColor(log.httpMethod), marginTop: 4, boxShadow: `0 0 0 3px ${timelineDotColor(log.httpMethod)}22` }} />
                                        ),
                                        children: (
                                            <div
                                                onClick={() => openDrawer(log)}
                                                style={{ cursor: "pointer", padding: "10px 16px", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, background: "#fff", marginBottom: 6, transition: "box-shadow 0.15s" }}
                                                className="hover:shadow-md"
                                            >
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 6 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                        <Avatar style={{ background: avatarColor(log.fullName), fontWeight: 700, fontSize: 12 }} size={30}>
                                                            {initials(log.fullName)}
                                                        </Avatar>
                                                        <div>
                                                            <span style={{ fontWeight: 600, color: TEXT_PRIMARY, fontSize: 13 }}>{log.fullName || "Unknown"}</span>
                                                            <span style={{ color: TEXT_MUTED, fontSize: 12, marginLeft: 6 }}>{log.email}</span>
                                                        </div>
                                                    </div>
                                                    <span style={{ color: TEXT_MUTED, fontSize: 11 }}>
                                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                        {formatDateTime(log.createdAt, "DD/MM/YYYY HH:mm AMPM")}
                                                    </span>
                                                </div>
                                                <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                                    <Tag color={HTTP_TAG_COLORS[log.httpMethod] ?? "default"} style={{ fontWeight: 700, borderRadius: 6, fontSize: 11 }}>{log.httpMethod}</Tag>
                                                    <span style={{ fontSize: 12, color: "#6366f1" }}>{MODULE_ICONS[log.moduleName] ?? <ApiOutlined />}</span>
                                                    <Text style={{ fontSize: 12, color: TEXT_MUTED }}>{log.moduleName}</Text>
                                                    <Text style={{ fontSize: 12, color: TEXT_PRIMARY, fontWeight: 500 }}>
                                                        {isLogin(log.actionName) && <LoginOutlined style={{ color: "#10b981", marginRight: 4 }} />}
                                                        {log.actionName}
                                                    </Text>
                                                    {(() => { const cfg = ROLE_CONFIG[log.userRoleName] ?? { color: "#64748b", bg: "#f1f5f9" }; return <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>{log.userRoleName}</span>; })()}
                                                </div>
                                            </div>
                                        ),
                                    }))}
                                />
                                <div style={{ marginTop: 8 }}>
                                    <Pagination currentPage={pageNumber.current} totalPages={totalPages} onPageChange={handlePageChange} />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Card>

            {/* ── Detail Drawer ────────────────────────────────────────────── */}
            <Drawer
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {drawerRecord && (
                            <Avatar style={{ background: avatarColor(drawerRecord.fullName), fontWeight: 700 }} size={36}>
                                {initials(drawerRecord.fullName)}
                            </Avatar>
                        )}
                        <div>
                            <div style={{ fontWeight: 700, color: TEXT_PRIMARY, fontSize: 15 }}>{drawerRecord?.fullName || "Activity Detail"}</div>
                            <div style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 400 }}>{drawerRecord?.email}</div>
                        </div>
                    </div>
                }
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                width={520}
                footer={
                    <Button block onClick={() => setDrawerVisible(false)}>Close</Button>
                }
            >
                {drawerRecord && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                        {/* Badges row */}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <Tag color={HTTP_TAG_COLORS[drawerRecord.httpMethod] ?? "default"} style={{ fontWeight: 700, borderRadius: 6, fontSize: 13, padding: "2px 14px" }}>
                                {drawerRecord.httpMethod}
                            </Tag>
                            {(() => { const cfg = ROLE_CONFIG[drawerRecord.userRoleName] ?? { color: "#64748b", bg: "#f1f5f9" }; return <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 20, padding: "2px 14px", fontSize: 13, fontWeight: 600 }}>{drawerRecord.userRoleName}</span>; })()}
                            {isLogin(drawerRecord.actionName) && <Tag icon={<LoginOutlined />} color="success">Login Event</Tag>}
                        </div>

                        {/* Info grid */}
                        <Card size="small" style={{ borderRadius: 12, background: "#f8fafc", border: `1px solid ${CARD_BORDER}` }} bodyStyle={{ padding: "14px 18px" }}>
                            {[
                                { label: "Module",    value: drawerRecord.moduleName },
                                { label: "Action",    value: drawerRecord.actionName },
                                { label: "Timestamp", value: formatDateTime(drawerRecord.createdAt, "DD/MM/YYYY HH:mm AMPM") },
                                { label: "User ID",   value: drawerRecord.userId },
                            ].map(row => (
                                <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${CARD_BORDER}` }}>
                                    <Text style={{ color: TEXT_MUTED, fontSize: 13, fontWeight: 500 }}>{row.label}</Text>
                                    <Text style={{ color: TEXT_PRIMARY, fontSize: 13, fontWeight: 600, maxWidth: 300, textAlign: "right", wordBreak: "break-all" }}>{row.value ?? "—"}</Text>
                                </div>
                            ))}
                        </Card>

                        {/* Payload */}
                        <div>
                            <Text style={{ color: TEXT_MUTED, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 8 }}>
                                Request Payload
                            </Text>
                            {renderPayload(drawerRecord.data)}
                        </div>
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default ActivityLogs;